"use client";

import { useState, useEffect } from "react";
import { useSelectedCustomer } from "../../utils/context/SelectedCustomerContext";
import {
  getCustomerByPhone,
  formatCurrency,
  formatDate,
} from "../../utils/data/customerData";

const CustomerImformationOutbound = () => {
  const { selectedCustomerId } = useSelectedCustomer();
  const [customer, setCustomer] = useState(null);
  const [callNotes, setCallNotes] = useState("");
  const [dispositionStatus, setDispositionStatus] = useState("");
  const [nextFollowUpDate, setNextFollowUpDate] = useState("");

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    loan: false,
    risk: false,
    ptp: false,
    communication: false,
    callManagement: false,
  });

  useEffect(() => {
    if (selectedCustomerId) {
      const customerData = getCustomerByPhone(selectedCustomerId);
      setCustomer(customerData);
      if (customerData) {
        setCallNotes(customerData.call_notes || "");
        setDispositionStatus(customerData.disposition_status || "");
        setNextFollowUpDate(customerData.next_follow_up_date || "");
      }
    }
  }, [selectedCustomerId]);

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const SectionHeader = ({ title, sectionKey, isExpanded }) => (
    <div
      className="flex items-center justify-between px-3 py-2 text-white bg-black rounded-t shadow cursor-pointer"
      onClick={() => toggleSection(sectionKey)}
    >
      <h3 className="text-xs font-semibold tracking-wide text-white font-inter">
        {title}
      </h3>
      <div className="text-[10px] text-gray-300">
        {isExpanded ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14l5-5 5 5z" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 10l5 5 5-5z" />
          </svg>
        )}
      </div>
    </div>
  );

  if (!customer) {
    return (
      <div className="w-[300px] h-screen bg-[#F9FAFB] border-l border-[#0000002B] flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="mb-2 text-lg">👤</div>
          <p className="text-sm">Select a customer to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[300px] h-screen bg-[#F9FAFB] border-l border-[#0000002B] overflow-y-auto">
      <div className="pl-4 border-b ">
        <h2 className="text-sm font-semibold text-black font-inter">
          Customer Details
        </h2>
      </div>
      <div className="py-4 pl-4 space-y-4 ">
        {[
          {
            key: "basic",
            title: "Basic Information",
            content: (
              <>
                <Info label="Customer ID / CIF" value={customer.customer_id} />
                <Info label="Name" value={customer.customer_name} />
                <Info label="Phone Number" value={customer.phone_number} />
                <Info
                  label="Language Preference"
                  value={customer.language_preference}
                />
              </>
            ),
          },
          {
            key: "loan",
            title: "Loan Information",
            content: (
              <>
                <Info
                  label="Loan Account ID"
                  value={customer.loan_account_id}
                />
                <Info label="Product Type" value={customer.product_type} />
                <Info
                  label="Loan Amount"
                  value={formatCurrency(customer.loan_amount)}
                />
                <Info
                  label="EMI Due Amount"
                  value={formatCurrency(customer.emi_due_amount)}
                  className="font-medium text-red-600"
                />
                <Info label="Due Date" value={formatDate(customer.due_date)} />
                <Info
                  label="Last EMI Paid Date"
                  value={formatDate(customer.last_emi_paid_date)}
                />
                <Info
                  label="Last Paid Amount"
                  value={formatCurrency(customer.last_paid_amount)}
                />
              </>
            ),
          },
          {
            key: "risk",
            title: "Risk Information",
            content: (
              <>
                <Info
                  label="DPD (Days Past Due)"
                  value={`${customer.dpd} days`}
                  className="font-medium"
                />
                <Info
                  label="Delinquency Status"
                  value={customer.delinquency_status}
                  className="font-medium"
                  style={{ color: customer.status_color || "#ef4444" }}
                />
                <Info
                  label="Credit Score Band"
                  value={customer.credit_score_band}
                />
                <Info
                  label="Fraud/Watchlist Flag"
                  value={customer.fraud_flag ? "⚠️ Flagged" : "✅ Clear"}
                  className={`font-medium ${
                    customer.fraud_flag ? "text-red-600" : "text-green-600"
                  }`}
                />
              </>
            ),
          },
          {
            key: "ptp",
            title: "PTP History",
            content: (
              <div className="p-2 rounded bg-gray-50 text-[10px] text-gray-700 font-inter">
                {customer.ptp_history || "No PTP history available"}
              </div>
            ),
          },
          {
            key: "communication",
            title: "Communication & Actions",
            content: (
              <>
                <Info
                  label="Communication Consent"
                  value={
                    customer.communication_consent
                      ? "✅ Granted"
                      : "❌ Not Granted"
                  }
                  className={
                    customer.communication_consent
                      ? "text-green-600"
                      : "text-red-600"
                  }
                />
                <Info
                  label="Repayment Link Sent"
                  value={
                    customer.repayment_link_sent ? "✅ Sent" : "❌ Not Sent"
                  }
                  className={
                    customer.repayment_link_sent
                      ? "text-green-600"
                      : "text-gray-600"
                  }
                />
                <Info
                  label="Waiver Requested"
                  value={
                    customer.waiver_requested ? "⚠️ Requested" : "No Request"
                  }
                  className={
                    customer.waiver_requested
                      ? "text-orange-600"
                      : "text-gray-600"
                  }
                />
              </>
            ),
          },
          {
            key: "callManagement",
            title: "Call Management",
            content: (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-gray-600 font-inter block mb-1">
                    Disposition Status
                  </label>
                  <select
                    value={dispositionStatus}
                    onChange={(e) => setDispositionStatus(e.target.value)}
                    className="w-full text-[10px] p-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select Status</option>
                    <option value="Connected">Connected</option>
                    <option value="Not Reachable">Not Reachable</option>
                    <option value="Busy">Busy</option>
                    <option value="Promise to Pay">Promise to Pay</option>
                    <option value="Dispute">Dispute</option>
                    <option value="Callback Requested">
                      Callback Requested
                    </option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-gray-600 font-inter block mb-1">
                    Call Notes
                  </label>
                  <textarea
                    value={callNotes}
                    onChange={(e) => setCallNotes(e.target.value)}
                    placeholder="Enter call notes..."
                    className="w-full text-[10px] p-2 border border-gray-300 rounded h-20 resize-none focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-600 font-inter block mb-1">
                    Next Follow-Up Date
                  </label>
                  <input
                    type="date"
                    value={nextFollowUpDate}
                    onChange={(e) => setNextFollowUpDate(e.target.value)}
                    className="w-full text-[10px] p-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                  />
                </div>
                <button className="w-full bg-blue-600 text-white text-[10px] font-medium py-2 rounded hover:bg-blue-700 transition">
                  Save Changes
                </button>
              </div>
            ),
          },
        ].map(({ key, title, content }) => (
          <div key={key} className="border  border-[#0000001A] rounded-lg">
            <SectionHeader
              title={title}
              sectionKey={key}
              isExpanded={expandedSections[key]}
            />
            {expandedSections[key] && (
              <div className="px-3 pb-3 space-y-2">{content}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Reusable Info component
const Info = ({ label, value, className = "", style = {} }) => (
  <div>
    <label className="text-[10px] text-gray-600 font-inter">{label}</label>
    <p
      className={`text-[11px] text-black font-inter ${className}`}
      style={style}
    >
      {value}
    </p>
  </div>
);

export default CustomerImformationOutbound;
